import { useCallback, useEffect, useRef, useState } from "react";

import { getGA4Ids, sendAdsConversionEvent } from "@common/analytics/ga4";
import { emptyArrayFor1Attach, resolveAttachmentUrls } from "@common/components/DetailsPane/helper";
import { IssueTableRowItem } from "@common/components/MasterTable/models/MasterTableModel";
import { TicketSortOption, UserSortOption } from "@common/components/ShakeTable/ui/SortHeaderCell";
import displayToast from "@common/components/Toast/displayToast";
import environment from "@common/environments";
import { useFeedbackAttributes } from "@common/hooks/filtering/modules/useFeedbackAttributes";
import { useAppBoundStorage } from "@common/hooks/filtering/useAppBoundStorage";
import useAbortController from "@common/hooks/useAbortController";
import { Page } from "@common/hooks/useShakePaginaton";
import { Attachment, SupportedFileTypes, Tag } from "@common/models";
import { CrashReport } from "@common/models/crashReporting";
import {
  fillActivityHistoryWithNetworkRequestType,
  generateActivityHistoryUnion,
  Log,
  LogUnionType,
} from "@common/models/eventTracking";
import { Integration, IntegrationType } from "@common/models/integrations";
import { Issue } from "@common/models/issueTracking";
import { AppNotificationType } from "@common/models/notifications";
import { TicketPriority } from "@common/models/TicketPriority.model";
import { TicketStatus } from "@common/models/TicketStatus.model";
import { resolveFileType } from "@common/util/files";
import { useAppDeps } from "@main/App.dependencies";
import useNotificationsApiConsumer from "@main/consumers/useNotificationsApiConsumer";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import {
  AiAssistant,
  aiAssistants,
} from "@main/pages/shared/components/SheldonSummary/AiAssistantButton/AiAssistantButton";
import { buildAiPromptFromIssue } from "@main/pages/shared/helpers/aiAssistantPrompt/promptBuilder";
import { useUserFeedbackDeps } from "@main/pages/UserFeedback/UserFeedback";
import { RoutePaths } from "@main/router/config/routePaths";
import { InfiniteData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { saveAs } from "file-saver";
import { generatePath, useLocation, useNavigate, useParams } from "react-router-dom";

interface Params {
  selectedWorkspaceId?: string;
  selectedAppId?: string;
  userFeedbackKey?: string;
  setRefreshActivity?: (refreshActivity: boolean) => void;
}

export default function useUserFeedbackDetailsApiConsumer<TData>({
  selectedWorkspaceId,
  selectedAppId,
  userFeedbackKey,
  setRefreshActivity,
}: Params) {
  const [error, setError] = useState<unknown>();

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { search } = useLocation();

  const { selectedWorkspace, selectedApp } = useAppSelectionContext();

  const { notificationsService } = useAppDeps();
  const { userFeedbackService } = useUserFeedbackDeps();

  const { validAttributes } = useFeedbackAttributes(true);
  const { fetchUnreadNotificationsAndDispatch, unreadNotifications } = useNotificationsApiConsumer();
  const initialUnreadRef = useRef(unreadNotifications);

  const { state: sortOption } = useAppBoundStorage<TicketSortOption | UserSortOption>("shakebugs.feedback_sort", "key");
  const sortValue = sortOption.value;
  const queryKey = `userFeedback ${selectedApp?.id}`;

  const deps = [selectedWorkspaceId, selectedAppId, validAttributes, sortValue];

  const { abortSignal } = useAbortController();
  const { workspaceSlug, appKey } = useParams<{ workspaceSlug: string; appKey: string }>();
  const [integrationDropdownOpened, setIntegrationDropdownOpened] = useState<IntegrationType | undefined>(undefined);
  const [sendingIntegrationMap, setSendingIntegrationMap] = useState(new Map<string, boolean>());
  const [mappedAttachments, setMappedAttachments] = useState<Attachment[]>([]);
  const autoOpenedAiAssistantRef = useRef<string | undefined>(undefined);

  const [activityHistory, setActivityHistory] = useState<LogUnionType[] | undefined>();
  const [isLoadingActivityHistory, setIsLoadingActivityHistory] = useState(false);

  const markNotificationsAsRead = useCallback(
    async (userFeedbackId: string) => {
      if (!selectedWorkspace || !selectedApp) return;

      const unreadNotificationList = initialUnreadRef.current[selectedApp.id]?.filter(
        (notification) =>
          notification.issue_id === userFeedbackId && notification.type === AppNotificationType.NEW_ISSUE,
      );

      if (!unreadNotificationList) return;

      for (const unreadNotification of unreadNotificationList) {
        await notificationsService.markNotificationAsRead(selectedWorkspace.id, unreadNotification.id);
      }

      await fetchUnreadNotificationsAndDispatch(selectedWorkspace.id);
    },
    [fetchUnreadNotificationsAndDispatch, notificationsService, selectedApp, selectedWorkspace],
  );

  async function getUserFeedbackServiceHooks() {
    if (!selectedWorkspaceId || !selectedAppId || !issue?.id) return;

    try {
      const { data } = await userFeedbackService.getServiceHooks(selectedWorkspaceId, selectedAppId, issue.id);

      return data;
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: "Please try again.",
      });
    }
  }

  const getUserFeedbackBlackbox = async () => {
    if (!selectedWorkspaceId || !selectedAppId || !issue?.id) return;

    try {
      const { data } = await userFeedbackService.getBlackbox(selectedWorkspaceId, selectedAppId, issue.id);

      return data;
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: "Please try again.",
      });
    }
  };

  const getLinkedIssues = async () => {
    if (!selectedWorkspaceId || !selectedAppId || !issue?.id || issue?.parent_id) return;

    try {
      const { data } = await userFeedbackService.getLinkedIssues(selectedWorkspaceId, selectedAppId, issue.id);

      return data;
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: "Please try again.",
      });
    }
  };

  const getUserFeedbackDetails = async () => {
    if (!selectedWorkspaceId || !selectedAppId || !userFeedbackKey) return undefined;

    setError(undefined);
    let ga4Ids: { clientId?: string; sessionId?: string } = {};
    try {
      ga4Ids = getGA4Ids();
    } catch {
      // GA4 ID retrieval failure should not block the main request
    }
    try {
      return await userFeedbackService
        .getUserFeedback(selectedWorkspaceId, selectedAppId, userFeedbackKey, abortSignal, ga4Ids)
        .then(({ data }) => {
          markNotificationsAsRead(data.id);
          return data as Issue;
        });
    } catch (error) {
      if (abortSignal.aborted) return undefined;
      setError(error);
      return undefined;
    }
  };

  const getUserFeedbackSuggestion = async () => {
    if (!selectedWorkspaceId || !selectedAppId || !issue || !issue.suggest_duplicate || issue.parent_id) return null;

    try {
      return await userFeedbackService
        .getUserFeedbackSuggestion(selectedWorkspaceId, selectedAppId, issue.id, abortSignal)
        .then(({ data }) => {
          return data as Issue;
        });
    } catch (error) {
      if (abortSignal.aborted) return undefined;
      return undefined;
    }
  };

  const issueDetailsQueryKey = [`ticket`, { selectedAppId, userFeedbackKey }];
  const { data: issue, refetch } = useQuery(issueDetailsQueryKey, getUserFeedbackDetails, {
    refetchOnWindowFocus: false,
    cacheTime: Infinity,
  });

  const getSentryProjects = useCallback(async () => {
    if (!selectedWorkspaceId || !selectedAppId || !selectedWorkspace?.sentry) return;

    const { data } = await userFeedbackService.getSentryInfo(selectedWorkspaceId);

    return data;
  }, [userFeedbackService, selectedAppId, selectedWorkspaceId, selectedWorkspace?.sentry]);

  const getSentryIssues = useCallback(
    async (projectSlug: string, environment: string) => {
      if (!selectedWorkspaceId || !selectedAppId || !issue?.id || !selectedWorkspace?.sentry) return;

      const { data } = await userFeedbackService.getSentryIssues(
        selectedWorkspaceId,
        selectedAppId,
        issue.id,
        projectSlug,
        environment,
      );

      return data;
    },
    [selectedAppId, selectedWorkspaceId, selectedWorkspace?.sentry, userFeedbackService, issue?.id],
  );

  const { data: issueSuggestion } = useQuery(
    [`ticket suggestion`, { selectedAppId, userFeedbackKey }],
    getUserFeedbackSuggestion,
    {
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
      enabled: Boolean(issue && issue.suggest_duplicate),
    },
  );

  const getUserFeedbackDetailsNums = async () => {
    if (!selectedWorkspaceId || !selectedAppId || !issue) return undefined;

    try {
      return await userFeedbackService
        .getUserFeedbackDetailsNums(selectedWorkspaceId, selectedAppId, issue?.id, abortSignal)
        .then(({ data }) => {
          return data;
        });
    } catch (error) {
      if (abortSignal.aborted) return undefined;
      return undefined;
    }
  };

  const { data: issueDetailsNums } = useQuery(
    [`ticket details num`, { selectedAppId, userFeedbackKey }],
    getUserFeedbackDetailsNums,
    {
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
      enabled: Boolean(issue),
    },
  );

  const { data: linkedIssues, refetch: refetchLinkedIssues } = useQuery(
    [`linked issues`, { selectedAppId, userFeedbackKey }],
    getLinkedIssues,
    {
      enabled: issue && issue.id ? true : false,
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
    },
  );

  const { data: serviceHooks, refetch: hooksRefetch } = useQuery(
    [`hooks`, { selectedAppId, userFeedbackKey }],
    getUserFeedbackServiceHooks,
    {
      enabled: issue && issue.id ? true : false,
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
    },
  );

  const { data: blackboxData } = useQuery([`blackbox`, { selectedAppId, userFeedbackKey }], getUserFeedbackBlackbox, {
    enabled: issue && issue.id ? true : false,
    refetchOnWindowFocus: false,
    cacheTime: Infinity,
  });

  const updateUserFeedback = async (issue: Partial<Issue>, isSuggestion?: boolean) => {
    if (!selectedWorkspaceId || !selectedAppId || !userFeedbackKey) return;

    return userFeedbackService
      .updateUserFeedback(selectedWorkspaceId, selectedAppId, userFeedbackKey, issue)
      .then(() => {
        if (isSuggestion) refetch();
        setRefreshActivity?.(true);
        return issue;
      });
  };

  const { mutate: mutateUserFeedbackTitle } = useMutation({
    mutationFn: updateUserFeedback,
    onSuccess: (changedIssue) => {
      updateIssueCache("pretty_title", changedIssue?.title);
      updateIssueDetailsCache("pretty_title", changedIssue?.title);
    },
  });

  const { mutate: mutateUserFeedbackSuggestion } = useMutation({
    mutationFn: (issue: Partial<Issue>) => updateUserFeedback(issue, true),
    onSuccess: (changedIssue) => updateIssueCache("suggest_duplicate", changedIssue?.suggest_duplicate),
  });

  const updateFeedbackPublic = async (isPublic: boolean) => {
    if (!selectedWorkspaceId || !selectedAppId || !userFeedbackKey) return;

    const { data } = await userFeedbackService.updateUserFeedbackPublic(
      selectedWorkspaceId,
      selectedAppId,
      userFeedbackKey,
      isPublic,
    );

    await refetch();
    setRefreshActivity?.(true);
    return data;
  };

  const { mutateAsync: mutateUserFeedbackPublicAsync } = useMutation({
    mutationFn: (isPublic: boolean) => updateFeedbackPublic(isPublic),
    onSuccess: (changedIssue) => {
      updateIssueCache("public_url", changedIssue?.public_url);
      updateIssueCache("public_key", changedIssue?.public_key);
    },
  });

  const deleteUserFeedback = async (id: string) => {
    if (!selectedWorkspaceId || !selectedAppId || !userFeedbackKey) return;

    if (Boolean(selectedApp?.is_sample)) {
      displayToast({ content: "Deleting sample tickets is not allowed." });
      return;
    }

    try {
      await userFeedbackService.deleteUserFeedback(selectedWorkspaceId, selectedAppId, userFeedbackKey);
      displayToast({ content: "Ticket has been successfully deleted" });
      navigate(
        generatePath(RoutePaths.USER_FEEDBACK, { workspaceSlug: workspaceSlug ?? null, appKey: appKey ?? null }),
        { replace: true },
      );
      return id;
    } catch (error) {
      displayToast({ title: "Something went wrong", content: "Please try again" });
    }
  };

  const { mutate: mutateFeedbackDelete } = useMutation({
    mutationFn: deleteUserFeedback,
    onSuccess: (ticketId) => removeIssueFromCache(ticketId),
  });

  const createIssueTag = async (tag: Tag) => {
    if (!selectedWorkspaceId || !issue?.id) return;

    const { data } = await userFeedbackService.createIssueTag(selectedWorkspaceId, issue.id, tag.name);
    refetch();
    setRefreshActivity?.(true);
    return data;
  };

  const { mutate: createTag } = useMutation({
    mutationFn: createIssueTag,
    onSuccess: (tags) => updateIssueCache("tags", tags ?? []),
  });

  const deleteIssueTag = async (tagName: string) => {
    if (!selectedWorkspaceId || !issue?.id) return;

    const { data } = await userFeedbackService.deleteIssueTag(selectedWorkspaceId, issue.id, tagName);
    refetch();
    setRefreshActivity?.(true);
    return data;
  };

  const { mutate: deleteTag } = useMutation({
    mutationFn: deleteIssueTag,
    onSuccess: (tags) => updateIssueCache("tags", tags ?? []),
  });

  const sendIssue = async (integration: Integration) => {
    if (!selectedApp || !selectedWorkspaceId || !issue?.id) return;

    try {
      setSendingIntegrationMap((map) => {
        return new Map(map.entries()).set(integration.id, true);
      });

      await userFeedbackService.sendIssue(selectedWorkspaceId, selectedApp.id, issue.id, integration.id);

      await hooksRefetch();
      setIntegrationDropdownOpened(undefined);
    } catch (error) {
      if (error.response.status === 403) {
        displayToast({
          title: "Something went wrong",
          content: error.response.data.message,
        });
        return;
      }
      displayToast({ title: "Something went wrong", content: "Please try again." });
    } finally {
      setSendingIntegrationMap((map) => {
        const newMap = new Map(map.entries());
        newMap.delete(integration.id);
        return newMap;
      });
    }
    return;
  };

  const changeStatus = async (status: TicketStatus) => {
    if (!selectedApp || !selectedWorkspaceId || !issue?.id) return;

    try {
      await userFeedbackService.changeStatus(selectedWorkspaceId, selectedApp.id, issue.id, status);

      await refetch();
      setRefreshActivity?.(true);
      return status;
    } catch (error) {
      displayToast({ title: "Something went wrong", content: "Please try again." });
    }
    return;
  };

  const { mutate: mutateStatus } = useMutation({
    mutationFn: changeStatus,
    onSuccess: (status) => updateIssueCache("status", { status: status }),
  });

  const changePriority = async (priority: TicketPriority) => {
    if (!selectedApp || !selectedWorkspaceId || !issue?.id) return;

    try {
      await userFeedbackService.changePriority(selectedWorkspaceId, selectedApp.id, issue.id, priority);

      await refetch();
      setRefreshActivity?.(true);
      return priority;
    } catch (error) {
      displayToast({ title: "Something went", content: "Please try again." });
    }
    return;
  };

  const { mutate: mutatePriority } = useMutation({
    mutationFn: changePriority,
    onSuccess: (priority) => updateIssueCache("priority", { priority: priority }),
  });

  const changeAssignee = async (assigneeId: string | null) => {
    if (!selectedApp || !selectedWorkspaceId || !issue?.id) return;

    try {
      await userFeedbackService.changeAssignee(selectedWorkspaceId, selectedApp.id, issue.id, assigneeId);

      await refetch();
      setRefreshActivity?.(true);
      return assigneeId;
    } catch (error) {
      displayToast({ title: "Something went wrong", content: "Please try again." });
    }
    return;
  };

  const { mutate: mutateAssignee } = useMutation({
    mutationFn: changeAssignee,
    onSuccess: (assignee) => updateIssueCache("assignee", { assignee_id: assignee }),
  });

  const onMergeAction = async (items: (Issue | CrashReport)[], item: CrashReport | Issue) => {
    if (!selectedApp?.id || !selectedWorkspace?.id) return;

    const toastId = Date.now().toString();

    return userFeedbackService
      .bulkMergeInto(
        selectedWorkspace.id,
        selectedApp.id,
        items.map((item) => item.id),
        item.id,
      )
      .then(() => {
        // eslint-disable-next-line
        queryClient.setQueryData<InfiniteData<TData>>([queryKey, { ...deps, queryKey }], (oldData: any) => ({
          ...oldData,
          pages: oldData.pages.map((page: Page<IssueTableRowItem>) => {
            const pageItems = page.items.map((ticket) =>
              ticket.id === item.id
                ? {
                    ...ticket,
                    linked_issues_count: ticket.linked_issues_count + 1,
                  }
                : ticket,
            );
            return { ...page, items: pageItems };
          }),
        }));

        displayToast({
          id: toastId,
          content: "Ticket has been marked as duplicate",
          duration: 2000,
        });

        refetch();
        setRefreshActivity?.(true);
      })
      .catch(() => {
        displayToast({
          id: toastId,
          content: "Error happened while marking ticket as duplicate",
          duration: 2000,
        });
        return [];
      });
  };

  const onUnMergeAction = async (items: (Issue | CrashReport)[], itemId: string) => {
    if (!selectedApp?.id || !selectedWorkspace?.id) return;

    const toastId = Date.now().toString();

    return userFeedbackService
      .unMergeTicket(
        selectedWorkspace.id,
        selectedApp.id,
        items.map((item) => item.id),
        itemId,
      )
      .then(() => {
        // eslint-disable-next-line
        queryClient.setQueryData<InfiniteData<TData>>([queryKey, { ...deps, queryKey }], (oldData: any) => ({
          ...oldData,
          pages: oldData.pages.map((page: Page<Issue>) => {
            const pageItems = page.items.map((ticket) =>
              ticket.id === itemId
                ? {
                    ...ticket,
                    linked_issues_count: ticket.linked_issues_count > 0 ? ticket.linked_issues_count - 1 : 0,
                  }
                : ticket,
            );
            return { ...page, items: pageItems };
          }),
        }));

        displayToast({
          id: toastId,
          content: "Duplicate status has been cleared",
          duration: 2000,
        });

        refetch();
        setRefreshActivity?.(true);
      })
      .catch(() => {
        displayToast({
          id: toastId,
          content: "Error happened while clearing duplicate status",
          duration: 2000,
        });
        return [];
      });
  };

  const getActivityHistory = useCallback(async (): Promise<LogUnionType[]> => {
    if (!selectedWorkspaceId || !selectedAppId || !userFeedbackKey) return [];
    if (activityHistory !== undefined) return activityHistory;

    let activityHistoryUnion: LogUnionType[] = [];
    try {
      setIsLoadingActivityHistory(true);

      const response = await userFeedbackService.getActivityHistory(
        selectedWorkspaceId,
        selectedAppId,
        userFeedbackKey,
      );
      const filledActivityHistory: Log = fillActivityHistoryWithNetworkRequestType(response.data, false);
      activityHistoryUnion = generateActivityHistoryUnion(filledActivityHistory).reverse();

      setActivityHistory(activityHistoryUnion);
    } catch (error) {
      console.error("Failed to get activity history", error);
    } finally {
      setIsLoadingActivityHistory(false);
    }

    return activityHistoryUnion;
  }, [activityHistory, selectedAppId, selectedWorkspaceId, userFeedbackKey, userFeedbackService]);

  const openAiAssistantWithPrompt = useCallback(
    async (assistant: AiAssistant, shouldOpenNewTab = true) => {
      if (!selectedWorkspaceId || !selectedAppId || !issue) return;

      const issueActivityHistory: LogUnionType[] = await getActivityHistory();
      const prompt: string = buildAiPromptFromIssue(issue, issueActivityHistory, selectedApp?.platform.name);
      const assistantUrl = assistant.url + encodeURIComponent(prompt);

      openAiAssistantUrl(assistantUrl, shouldOpenNewTab);
    },
    [getActivityHistory, issue, selectedApp?.platform.name, selectedAppId, selectedWorkspaceId],
  );

  useEffect(() => {
    const action = new URLSearchParams(search).get("action");
    const selectedAssistant = findAiAssistantByName(action);

    if (!issue?.id || !selectedAssistant) return;

    const autoOpenKey = `${issue.id}-${selectedAssistant.name}`;
    if (autoOpenedAiAssistantRef.current === autoOpenKey) return;

    autoOpenedAiAssistantRef.current = autoOpenKey;
    void openAiAssistantWithPrompt(selectedAssistant, false);
  }, [issue?.id, openAiAssistantWithPrompt, search]);

  const downloadActivityHistory = async (ticketKey?: string) => {
    if (!selectedApp || !selectedWorkspaceId || !ticketKey) return;

    try {
      const { data } = await userFeedbackService.downloadActivityHistory(
        selectedWorkspaceId,
        selectedApp.id,
        ticketKey,
      );

      const blob = new Blob([data], { type: "text/plain;charset=utf-8" });
      saveAs(blob, `${issue?.pretty_title}.log`);

      displayToast({
        title: "Fantastic!",
        content: `Activity history has been successfully downloaded`,
      });
    } catch (error) {
      displayToast({ title: "Something went wrong", content: "Please try again." });
    }
    return;
  };

  const mapAttachment = useCallback(async (url: string) => {
    if (resolveFileType(url) === SupportedFileTypes.TXT) {
      await fetch(url, { method: "GET", mode: "cors" })
        .then((response) => response.text())
        .then((t) => {
          setMappedAttachments((mappedAttachments) => {
            return [...mappedAttachments, { url: url, text: t, num: 3 }];
          });
        })
        .catch(() => console.error("err"));
    } else {
      setMappedAttachments((mappedAttachments) => {
        return [...mappedAttachments, { url: url, num: getAttachmentOrderNum(resolveFileType(url)) }];
      });
    }
  }, []);

  const updateIssueCache = <T,>(field: string, data: T) =>
    // eslint-disable-next-line
    queryClient.setQueryData<InfiniteData<TData>>([queryKey, { ...deps, queryKey }], (oldData: any) => ({
      ...oldData,
      pages: oldData.pages.map((page: Page<Issue>) => {
        const pageItems = page.items.map((ticket) =>
          ticket.id === issue?.id
            ? {
                ...ticket,
                [field]: data,
              }
            : ticket,
        );
        return { ...page, items: pageItems };
      }),
    }));

  const removeIssueFromCache = (ticketId: string | undefined) => {
    if (!ticketId) return;
    // eslint-disable-next-line
    return queryClient.setQueryData<InfiniteData<TData>>([queryKey, { ...deps, queryKey }], (oldData: any) => ({
      ...oldData,
      pages: oldData.pages.map((page: Page<Issue>) => {
        const pageItems = page.items.filter((ticket) => ticket.id !== ticketId);
        return { ...page, items: pageItems };
      }),
    }));
  };

  const updateIssueDetailsCache = <T,>(field: string, data: T) => {
    // eslint-disable-next-line
    return queryClient.setQueryData<InfiniteData<TData>>(issueDetailsQueryKey, (oldData: any) => ({
      ...oldData,
      [field]: data,
    }));
  };

  useEffect(() => {
    sendAdsConversionEvent(environment.adsPageVisitConversionTagId);
  }, [selectedApp]);

  useEffect(() => {
    if (issue && !mappedAttachments.length) {
      resolveAttachmentUrls(issue)?.map((url) => {
        mapAttachment(url);
      });

      if (resolveAttachmentUrls(issue).length === 1 && issue.files.length === 0) {
        setMappedAttachments((mappedAttachments) => {
          return [...mappedAttachments, ...emptyArrayFor1Attach("large", selectedApp?.platform.name)];
        });
      }

      if (
        !Boolean(resolveAttachmentUrls(issue).length) ||
        (resolveAttachmentUrls(issue).length === 2 && !issue.files.length)
      ) {
        setMappedAttachments((mappedAttachments) => {
          return [...mappedAttachments, ...emptyArrayFor1Attach("small", selectedApp?.platform.name)];
        });
      }
    }
  }, [issue, mapAttachment, mappedAttachments, selectedApp?.platform.name]);

  return {
    userFeedbackDetails: issue,
    updateUserFeedbackTitle: mutateUserFeedbackTitle,
    updateFeedbackSuggestion: mutateUserFeedbackSuggestion,
    updateUserFeedbackPublicAsync: mutateUserFeedbackPublicAsync,
    deleteUserFeedback: mutateFeedbackDelete,
    createIssueTag: createTag,
    deleteIssueTag: deleteTag,
    sendIssue,
    serviceHooks,
    changeStatus: mutateStatus,
    changePriority: mutatePriority,
    changeAssignee: mutateAssignee,
    openAiAssistantWithPrompt,
    isLoadingActivityHistory,
    integrationDropdownOpened,
    setIntegrationDropdownOpened,
    sendingIntegrationMap,
    downloadActivityHistory,
    error,
    mappedAttachments,
    blackbox: blackboxData,
    onMergeAction,
    getSentryProjects,
    getSentryIssues,
    linkedIssues,
    refetchLinkedIssues,
    onUnMergeAction,
    issueDetailsNums,
    issueSuggestion,
  };
}

const getAttachmentOrderNum = (type?: SupportedFileTypes) => {
  switch (type) {
    case SupportedFileTypes.IMAGE:
    case SupportedFileTypes.VIDEO:
      return 1;
    case SupportedFileTypes.TXT:
      return 2;
    default:
      return 3;
  }
};

const findAiAssistantByName = (assistantName: string | null): AiAssistant | undefined => {
  if (!assistantName) return undefined;

  return aiAssistants.find((assistant) => assistant.name.toLowerCase() === assistantName.toLowerCase());
};

const openAiAssistantUrl = (url: string, shouldOpenNewTab: boolean) => {
  shouldOpenNewTab ? window.open(url, "_blank") : (window.location.href = url);
};
